/**
 * UI copy dictionary (Turkish only). Components read strings from here so a
 * future locale layer swaps dictionaries, not JSX. Each screen keeps its strings
 * under its own key group.
 */
import { APP_NAME, APP_TAGLINE } from '@/brand';

export const tr = {
  /**
   * Refusals that can reach almost any screen, so the wording lives once.
   * "Lütfen tekrar deneyin" is wrong for these: retrying changes nothing, and
   * telling the user to retry hides the one thing they can actually act on.
   */
  refusals: {
    accountPassive: 'Hesabınız pasif durumda. Bu işlemi yapabilmek için hesabınızı aktif edin.',
    accountPassiveAction: 'Hesabımı Aktif Et',
    publishingBlocked:
      'Hesabınızın ilan yayınlama yetkisi kapalı. Destek ile iletişime geçebilirsiniz.',
  },
  app: {
    name: APP_NAME,
    tagline: APP_TAGLINE,
  },
  /** Labels for the shared il/ilçe/mahalle cascade, on every screen that uses it. */
  location: {
    city: 'İl',
    cityPlaceholder: 'İl Seçiniz',
    district: 'İlçe',
    districtPlaceholder: 'İlçe Seçiniz',
    neighbourhood: 'Mahalle',
    neighbourhoodPlaceholder: 'Mahalle Seçiniz',
    loadError: 'Liste yüklenemedi.',
    retry: 'Tekrar dene',
  },
  nav: {
    explore: 'Keşfet',
    chat: 'Sohbet',
    createListing: 'İlan Ver',
    report: 'Sorun Bildir',
    profile: 'Profil',
  },
  common: {
    close: 'Kapat',
    back: 'Geri',
    favorite: 'Favori',
    goToDetail: 'Detaya Git',
    sendMessage: 'Mesaj Gönder',
    callNow: 'Hemen Ara',
    contactError: {
      unavailable: 'Bu ilana ait numara şu an mevcut değil',
      tooMany: 'Çok fazla deneme, biraz sonra tekrar deneyin',
      generic: 'Numara alınamadı, lütfen tekrar deneyin',
    },
    all: 'Tümü',
    /**
     * Ay adları — Türkçe'de ay adları her zaman büyük harfle başlar. Kaynak
     * listesi burada; hasat aralığı bunlardan üretilir (util/harvestRange.ts).
     */
    months: [
      'Ocak',
      'Şubat',
      'Mart',
      'Nisan',
      'Mayıs',
      'Haziran',
      'Temmuz',
      'Ağustos',
      'Eylül',
      'Ekim',
      'Kasım',
      'Aralık',
    ],
    distanceAway: (km: string) => `~${km} km uzaklıkta`,
    quantity: 'Miktar',
    active: 'Aktif',
    passive: 'Pasif',
    makePassive: 'Pasif Yap',
    loadMore: 'Daha fazla yükle',
    loadMoreRemaining: (n: number) => `${n} tane daha`,
  },
  explore: {
    searchPlaceholder: 'Ürün, kategori veya üretici ara…',
    searchClear: 'Aramayı temizle',
    showList: 'Liste Göster',
    productLabel: 'Ürün filtresi',
    productPlaceholder: 'Ürün seçin',
    /**
     * First row of a category chip's menu — clears the subcategory and leaves
     * the category itself selected. Deliberately category-independent: "Tüm
     * Kuruyemiş" would need the category name to agree in number, and the
     * names in the data are inconsistent about that ("Meyveler" but
     * "Kuruyemiş"). Inside one category's own menu this is unambiguous.
     */
    allProducts: 'Tüm ürünler',
    showMap: 'Harita Göster',
    resultCount: (n: number) => `${n} ilan`,
    emptyTitle: 'Bu filtrede ilan bulunamadı',
    emptyToast: 'Bu filtrede ilan bulunamadı',
    filtersClearedToast: 'Arama sonucunu göstermek için filtreler temizlendi',
    emptyHint: 'Filtreleri genişletmeyi deneyin veya ilk ilanı siz verin.',
    noListingsTitle: 'Şu an yayında ilan yok',
    noListingsHint:
      'Yeni ilanlar eklendikçe burada görünecek. Satacak ürününüz varsa hemen ilan verin.',
    mapHint: 'Haritada işaretli noktalara tıklayarak bölgelere yakınlaşıp ilanları görebilirsiniz.',
    filters: {
      title: 'Filtreler',
      priceRange: 'Fiyat Aralığı (TL)',
      minPrice: 'En Az',
      maxPrice: 'En Çok',
      listedWithin: 'İlan Tarihi',
      listedWithinOptions: { '1d': 'Son 24 saat', '7d': 'Son 7 gün', '30d': 'Son 30 gün' },
      apply: 'Uygula',
      clear: 'Temizle',
    },
    suggest: {
      listings: 'İlanlar',
      producers: 'Üreticiler',
      categories: 'Kategoriler',
      products: 'Alt Kategoriler',
      empty: 'Eşleşen sonuç yok',
    },
  },
  // Shown when the backend refused a request and said why. Retrying does not
  // help for any of these, so none of them says 'tekrar deneyin'.
  apiErrors: {
    invalidInput: 'Girdiğiniz bilgilerde bir hata var. Lütfen alanları kontrol edin.',
    network: 'Bağlantı kurulamadı. İnternetinizi kontrol edip tekrar deneyin.',
    productCategoryMismatch:
      'Seçtiğiniz ürün, seçtiğiniz kategoriye ait değil. Ürünü yeniden seçin.',
    productMissing: 'Seçtiğiniz ürün artık listede yok. Lütfen yeniden seçin.',
    neighbourhoodMismatch: 'Seçtiğiniz mahalle, seçtiğiniz ilçeye ait değil. Konumu yeniden seçin.',
    neighbourhoodMissing: 'Seçtiğiniz mahalle bulunamadı. Konumu yeniden seçin.',
    listingDailyLimit:
      'İlan verme sınırına ulaştınız. Yeni ilanınızı daha sonra verebilirsiniz.',
    fields: {
      title: 'Başlık çok uzun. En fazla 150 karakter olabilir.',
      description: 'Açıklama çok uzun. En fazla 5000 karakter olabilir.',
      productType: 'Ürün cinsi çok uzun. En fazla 100 karakter olabilir.',
      price: 'Fiyat geçerli bir sayı olmalı ve sıfırdan büyük olmalı.',
      quantity: 'Miktar geçerli bir sayı olmalı ve sıfırdan büyük olmalı.',
      harvestYear: 'Hasat yılı 2000 ile 2100 arasında olmalı.',
      location: 'Konum bilgisi eksik veya geçersiz. İl, ilçe ve mahalleyi yeniden seçin.',
      duration: 'İlan süresi 1, 3 veya 6 ay olabilir.',
    },
  },
  /**
   * Shown when the backend ends the session mid-use because an admin banned or
   * deactivated the account.
   *
   * COPY RULE: claim only what is actually true. Listings are HIDDEN by the
   * visibility predicate, not deleted — the rows survive and reappear on unban,
   * so "kaldırıldı, silinmedi" is the honest wording. Nothing here promises the
   * user cannot be contacted at all: existing chat threads still accept messages
   * from the other side, the banned user simply cannot read or answer them.
   */
  sessionRevoked: {
    title: 'Oturumunuz sonlandırıldı',
    expiredTitle: 'Oturum süreniz doldu',
    expired: 'Güvenlik için bir süre sonra oturum kapanıyor. Devam etmek için tekrar giriş yapın.',
    expiredDraft:
      'Doldurduğunuz ilan formu kaydedildi, giriş yaptıktan sonra kaldığınız yerden devam edebilirsiniz.',
    banned: 'Hesabınız yönetici tarafından yasaklandı.',
    deactivated: 'Hesabınız yönetici tarafından pasife alındı.',
    missing: 'Hesabınız artık mevcut değil.',
    reason: (text: string) => `Neden: ${text}`,
    listings: 'İlanlarınız yayından kaldırıldı, silinmedi.',
    dismiss: 'Tamam',
  },
  auth: {
    emailPlaceholder: 'ornek@eposta.com',
    emailOptional: 'E-Posta Adresi (isteğe bağlı)',
    emailOptionalHint: 'Daha iyi iletişim için e-posta ekleyin.',
    password: 'Parola',
    passwordPlaceholder: '••••••',
    passwordHint: 'En az 8 karakter; büyük/küçük harf, rakam ve özel karakter içermeli.',
    passwordConfirm: 'Parola (Tekrar)',
    passwordMismatch: 'Parolalar eşleşmiyor.',
    phoneInvalid: 'Geçerli bir cep telefonu girin (5xx xxx xx xx).',
    otpTitle: 'Telefonunuzu Doğrulayın',
    otpBody: (phone: string) => `${phone} numarasına 6 haneli bir doğrulama kodu gönderdik.`,
    otpLabel: 'Doğrulama Kodu',
    otpPlaceholder: '______',
    otpSubmit: 'Doğrula',
    otpVerifying: 'Doğrulanıyor…',
    otpResend: 'Kodu tekrar gönder',
    otpResendIn: (time: string) => `Tekrar göndermek için ${time}`,
    otpExhausted:
      'Bu numara için doğrulama hakkınız doldu. 24 saat sonra tekrar deneyebilir veya destek ile iletişime geçebilirsiniz.',
    otpRestart: 'Bilgileri değiştir',
    verified: 'Doğrulandı',
    notVerified: 'Doğrulanmadı',
    verifyEmailAction: 'Doğrula',
    verifyEmailSending: 'Gönderiliyor…',
    verifyEmailSent: 'Doğrulama e-postası gönderildi.',
    verifyEmailFailed: 'E-posta gönderilemedi. Lütfen tekrar deneyin.',
    forgotPassword: 'Şifremi Unuttum',
    loginCta: 'Uygulamaya Giriş Yap',
    or: 'veya',
    gmail: 'Gmail',
    noAccount: 'Hesabınız yok mu?',
    registerLink: 'Kayıt Olun',
    registerTitle: 'Kayıt Ol',
    fullName: 'Ad Soyad',
    fullNamePlaceholder: 'Adınız Soyadınız',
    email: 'E-Posta Adresi',
    identifier: 'E-Posta veya Telefon',
    identifierPlaceholder: 'ornek@eposta.com veya 5__ ___ __ __',
    phone: 'Telefon Numarası',
    phonePlaceholder: '(5__) ___ __ __',
    phoneMustStartWith5: 'Cep telefonu 5 ile başlamalıdır.',
    kvkkConsent:
      'KVKK aydınlatma metnini okudum; kişisel verilerimin bu kapsamda işlenmesini onaylıyorum.',
    registerCta: 'Kayıt Ol',
    googleTitle: 'Kaydınızı Tamamlayın',
    googleBody:
      'Google hesabınızı doğruladık. Alıcıların size ulaşabilmesi için telefon numaranız gerekiyor.',
    googleCta: 'Kaydı Tamamla',
    googleTokenMissing:
      'Bağlantı geçersiz veya süresi dolmuş. Lütfen Google ile girişi tekrar deneyin.',
    googleErrors: {
      failed: 'Google ile giriş tamamlanamadı. Lütfen tekrar deneyin.',
      banned: 'Bu hesap askıya alınmış. Destek ile iletişime geçin.',
      inactive: 'Bu hesap kapatılmış. Destek ile iletişime geçin.',
      emailUnverified:
        'Google hesabınızın e-posta adresi doğrulanmamış. Önce Google üzerinden doğrulayın.',
    },
    haveAccount: 'Zaten hesabınız var mı?',
    loginLink: 'Giriş Yapın',
    verifyTitle: 'E-Postanı Doğrula',
    verifyBody: (email: string) =>
      `${email} adresine bir doğrulama bağlantısı gönderdik. Hesabını etkinleştirmek için gelen kutunu kontrol et.`,
    verifySuccessTitle: 'E-Posta Doğrulandı',
    verifySuccessBody: 'Hesabınız etkinleştirildi. Artık giriş yapabilirsiniz.',
    verifyErrorTitle: 'Bağlantı Geçersiz',
    verifyErrorBody:
      'Doğrulama bağlantısı geçersiz veya süresi dolmuş. E-posta adresinizi girin, yeni bir bağlantı gönderelim.',
    resend: 'Tekrar Gönder',
    resent: 'Gönderildi ✓',
    backToLogin: 'Giriş ekranına dön',
    goToLogin: 'Giriş Yap',
    forgot: {
      title: 'Şifremi Unuttum',
      body: 'Kayıtlı e-posta adresinizi girin; şifre sıfırlama bağlantısı gönderelim.',
      cta: 'Sıfırlama Bağlantısı Gönder',
      sentTitle: 'E-Posta Gönderildi',
      sentBody: (email: string) =>
        `${email} sistemde kayıtlıysa şifre sıfırlama bağlantısı gönderdik. Gelen kutunuzu kontrol edin.`,
      // The two branches of Şifremi Unuttum. Phone exists for the accounts
      // registered without an email address at all.
      chooseTitle: 'Şifrenizi nasıl sıfırlamak istersiniz?',
      byEmail: 'E-posta ile',
      byPhone: 'SMS ile',
      phoneBody: 'Kayıtlı telefon numaranızı girin; doğrulama kodu gönderelim.',
      phoneCta: 'Kod Gönder',
      codeTitle: 'Doğrulama Kodu',
      codeBody: (phone: string) => `${phone} numarasına 6 haneli bir kod gönderdik.`,
      codeCta: 'Doğrula',
      phoneUnknown: 'Bu numarayla kayıtlı bir hesap bulunamadı.',
    },
    reset: {
      title: 'Yeni Parola Belirle',
      newPassword: 'Yeni Parola',
      cta: 'Parolayı Güncelle',
      invalidTitle: 'Bağlantı Geçersiz',
      invalidBody: 'Şifre sıfırlama bağlantısı geçersiz veya süresi dolmuş.',
      requestNew: 'Yeni bağlantı iste',
    },
    menu: {
      profile: 'Profil',
      logout: 'Çıkış Yap',
    },
    errors: {
      invalidCredentials: 'E-posta veya parola hatalı.',
      // A ban is the only thing that blocks login — a passive account signs in
      // normally so its owner can reopen it.
      accountBanned: 'Hesabınız yasaklanmış. Destek ile iletişime geçin.',
      // Blacklist refusal at signup. Says nothing about why: the person on the
      // other side may not be the banned one, and the reason is not theirs to
      // read from a public form.
      signupRefused: 'Bu bilgilerle hesap oluşturulamıyor. Destek ile iletişime geçebilirsiniz.',
      // Google-created accounts have no password. Static, shown under the login
      // form for everyone — naming it per-account would turn the screen into an
      // e-posta enumeration oracle.
      noPasswordHint:
        'Google ile kayıt olduysanız "Google ile Giriş" kullanın veya Şifremi Unuttum ile şifre belirleyin.',
      emailTaken: 'Bu e-posta veya telefon numarası zaten kayıtlı.',
      tooManyRequests: 'Çok fazla deneme yapıldı. Lütfen biraz sonra tekrar deneyin.',
      // Out of guesses means the password is forgotten; point to the way out
      // rather than only asking them to wait.
      tooManyLoginAttempts:
        'Çok fazla hatalı deneme. Şifrenizi hatırlamıyorsanız Şifremi Unuttum ile yenileyebilirsiniz.',
      network: 'Bağlantı hatası. İnternet bağlantınızı kontrol edin.',
      generic: 'Bir şeyler ters gitti. Lütfen tekrar deneyin.',
    },
  },
  gallery: {
    open: 'Fotoğrafları Görüntüle',
    previous: 'Önceki Fotoğraf',
    next: 'Sonraki Fotoğraf',
    // Also in common.close. Repeated here so the whole group can be handed to
    // PhotoGallery as one labels object, which is what lets it mirror the panel.
    close: 'Kapat',
    counter: (current: number, total: number) => `${current} / ${total}`,
  },
  listing: {
    produceStatus: { onBranch: 'Dalında', inCrate: 'Kasada' },
    /**
     * Ambalaj sözcüğü kategoriye göre değişir.
     *
     * Sadece `inCrate` etiketini etkiler — `Dalında` her kategoride sabittir,
     * seçenek sayısı hep iki, ve varsayılan seçim hep ilk seçenektir. Yani bu
     * tablo yalnızca bir çeviri sözlüğü; veritabanında saklanan değer
     * ('inCrate') hiç değişmiyor, o yüzden migration gerekmiyor.
     *
     * YENİ KATEGORİ EKLEMEK = buraya bir satır eklemek. Kategori ADI ile
     * eşleşir (id ile değil: id'ler her ortamda ayrı seed ediliyor, ad ise
     * `categories.name` üzerinde UNIQUE). Listede olmayan kategori aşağıdaki
     * `produceStatus.inCrate` değerine düşer.
     */
    packagingByCategory: {
      Baklagiller: 'Çuvalda',
    } as Record<string, string>,
    priceUnit: { kg: 'kg', ton: 'ton', adet: 'adet' },
    producerDescription: 'Üretici Açıklaması',
    product: 'Ürün',
    productType: 'Ürün Cinsi',
    availableQuantity: 'Mevcut Miktar',
    // Deliberately does not name the levels: the value under it lists whichever
    // levels a listing has, and a label that enumerates them goes stale the
    // moment one is added.
    location: 'Konum',
    condition: 'Durum',
    listedAt: 'İlan Tarihi',
    harvestYear: 'Hasat Yılı',
    detail: {
      yearBadge: (y: number) => `Yıl: ${y}`,
      producerLabel: 'Üretici',
      details: 'Detaylar',
      preview: 'Önizleme',
      report: 'İlanı bildir',
      reportSignIn: 'Bildirmek için giriş yapın.',
      notFound: 'İlan bulunamadı',
      backToExplore: 'Keşfet ekranına dön',
      // One page for gone and never-existed: the server answers both the same way on purpose.
      unavailable: {
        title: 'Bu ilan şu an yayında değil',
        body: 'Satıcı ilanı kaldırmış ya da bir süreliğine durdurmuş olabilir. Benzer ürünleri Keşfet ekranında bulabilirsiniz.',
        explore: 'Keşfet’e Dön',
        favorites: 'Favorilerime Git',
      },
      share: {
        label: 'Paylaş',
        whatsapp: 'WhatsApp',
        copy: 'Bağlantıyı kopyala',
        copied: 'Bağlantı kopyalandı',
        copyFailed: 'Bağlantı kopyalanamadı',
      },
    },
  },
  searchSelect: {
    searchPlaceholder: 'Ara',
    noResults: 'Sonuç bulunamadı',
    more: (count: number) => `${count} ürün daha`,
    clear: 'Seçimi temizle',
  },

  createListing: {
    title: 'Yeni Ürün İlanı Ver',
    cancel: 'Vazgeç',
    clear: 'Temizle',
    photosLabel: (n: number, max: number) => `Ürün Fotoğrafları (${n}/${max})`,
    // Named rather than counted when it is one file: the producer has to know
    // which photo to swap out, and its filename is the only handle they have.
    photoTooLarge: (names: string[], maxMb: number) =>
      names.length === 1
        ? `"${names[0]}" çok büyük ve eklenmedi. Her fotoğraf en fazla ${maxMb} MB olabilir.`
        : `${names.length} fotoğraf çok büyük olduğu için eklenmedi. Her fotoğraf en fazla ${maxMb} MB olabilir.`,
    addPhoto: 'Fotoğraf',
    removePhoto: 'Fotoğrafı kaldır',
    photoAlt: (n: number) => `İlan fotoğrafı ${n}`,
    // The automatic photo review. Named like photoTooLarge: with several
    // photos picked at once, the filename is the producer's only handle.
    photoChecking: 'Fotoğraf kontrol ediliyor',
    photoRejectedNsfw: (names: string[]) =>
      names.length === 1
        ? `"${names[0]}" uygunsuz içerik barındırdığı için eklenmedi.`
        : `${names.length} fotoğraf uygunsuz içerik barındırdığı için eklenmedi.`,
    photoRejectedNotProduce: (names: string[]) =>
      names.length === 1
        ? `"${names[0]}" bir tarım ürünü fotoğrafına benzemiyor ve eklenmedi. Lütfen satacağınız ürünün fotoğrafını ekleyin.`
        : `${names.length} fotoğraf tarım ürünü fotoğrafına benzemediği için eklenmedi. Lütfen satacağınız ürünün fotoğrafını ekleyin.`,
    // Says "önerildi", not "belirlendi": the producer owns these fields and
    // is expected to correct them.
    photoAutoFilled: 'Bazı alanlar fotoğrafınıza göre dolduruldu. Lütfen kontrol edin.',
    category: 'Kategori',
    listingTitle: 'İlan Başlığı',
    listingTitlePlaceholder: 'Örn: Kadınhanı Sert Kırmızı Buğdayı',
    productType: 'Ürün Cinsi / Türü',
    productTypePlaceholder: 'Örn: Bezostaja, Amasya, Pembe Sera',
    product: 'Ürün',
    productPlaceholder: 'Ürün seçin',
    quantityAndUnit: 'Miktar & Birim',
    quantityPlaceholder: 'Miktar',
    unit: 'Birim',
    price: 'Fiyat & Birim',
    priceUnit: 'Fiyat birimi',
    produceStatus: 'Yetiştirme / Ambalaj Durumu',
    neighbourhoodRequired: 'Lütfen mahalle seçiniz.',
    stepsLabel: 'İlan adımları',
    stepProduct: 'Ürün Bilgileri',
    stepLocation: 'Konum ve Yayın',
    // Edit mode has no validity period to set, so the tab is just the location.
    stepLocationEdit: 'Konum',
    next: 'Devam',
    catalogLoadError: 'Ürünler yüklenemedi.',
    retry: 'Tekrar dene',
    productRequired: 'Lütfen ürün seçin.',
    customProductNameCancel: 'Listeden seç',
    customProductNamePlaceholder: 'Listede olmayan ürünün adı',
    customProductNameRequired: 'Ürün adını yazın.',
    titleRequired: 'Lütfen ilan başlığını girin.',
    productTypeRequired: 'Lütfen ürün cinsini girin.',
    harvestYearInvalid: 'Lütfen geçerli bir hasat yılı girin.',
    locationApproximate: 'Konum yaklaşık',
    locationApproximateNote: 'Haritada işaretlemezseniz ilanınız mahalle merkezinde gösterilir.',
    // Neutral on purpose: a pin outside the chosen mahalle is a legitimate
    // answer (fields sit on borders), so this asks a question rather than
    // reporting a mistake.
    locationPinMismatchTitle: 'Pin başka bir mahallede',
    locationPinMismatch: (picked: string) =>
      `Pin'i ${picked} sınırlarına bıraktınız. Mahalle seçiminiz güncellensin mi?`,
    locationPinMismatchConfirm: 'Güncelle',
    locationPinMismatchCancel: 'Seçimimi koru',
    location: 'Konum',
    locationRequired: 'Lütfen ürününüzün konumunu haritada işaretleyin.',
    priceInvalid: 'Lütfen geçerli bir fiyat girin.',
    quantityInvalid: 'Lütfen geçerli bir miktar girin.',
    locationUseMine: 'Konumumu Kullan',
    locationLocating: 'Konumunuz alınıyor…',
    locationDenied: 'Konumunuza erişilemedi — haritada işaretleyin.',
    locationExpand: 'Haritayı büyüt',
    locationClear: 'Pini Kaldır',
    locationCollapse: 'Tamam',
    locationExpandedTitle: 'Konum Seçin',
    harvestYear: 'Hasat Yılı',
    harvestRange: 'Hasat Zaman Aralığı',
    harvestRangePlaceholder: 'Ay Seçiniz',
    harvestRangeHint: 'Başlangıç ve bitiş ayına dokunun',
    // Kasım – Ocak gibi yıl sonunu aşan aralıklar geçerlidir, o yüzden
    // "bitiş başlangıçtan sonra olmalı" gibi bir kural yok.
    harvestRangeIncomplete: 'Bitiş ayını seçin',
    description: 'Özet Açıklama',
    descriptionPlaceholder:
      'Ürününüzün kalitesi, sevkiyat detayları ve ödeme yöntemleri hakkında alıcılara detaylı bilgi verin…',
    duration: 'Geçerlilik Süresi',
    durationMonths: (n: number) => `${n} Ay`,
    durationNote: 'Otomatik Sonlanma',
    submit: 'İlanı Sistemde Yayınla',
    submitError: 'İlan gönderilemedi. Lütfen tekrar deneyin.',
    photoUploadFailed: 'İlanınız alındı, ancak fotoğraflar yüklenemedi.',
    photosRejectedOnUpload: (n: number) =>
      `İlanınız alındı, ancak ${n} fotoğraf kontrolden geçemediği için eklenmedi.`,
    successTitle: 'İlanınız Alındı',
    successBody:
      'İlanınız onay sürecine iletildi. Kontrolden geçtikten sonra sistemde yayınlanacak.',
    successNew: 'Yeni İlan Ver',
    backToExplore: 'Keşfete Dön',
    editTitle: 'İlanı Düzenle',
    editNote: 'Değişiklikleriniz kaydedildikten sonra ilanınız yeniden incelemeye alınır.',
    editSubmit: 'Değişiklikleri Kaydet',
    editSubmitError: 'Değişiklikler kaydedilemedi. Lütfen tekrar deneyin.',
    editSuccessTitle: 'Değişiklikler Kaydedildi',
    editSuccessBody:
      'İlanınız yeniden incelemeye gönderildi. Onaylandıktan sonra tekrar yayında olacak.',
    // An admin's save is published without moderation.
    publishedTitle: 'İlanınız Yayında',
    publishedBody: 'İlanınız yayınlandı ve artık herkes tarafından görülebilir.',
    editPublishedBody: 'Değişiklikleriniz yayında.',
    backToListing: 'İlana Dön',
    existingPhotoDeleteFailed: 'Fotoğraf silinemedi. Lütfen tekrar deneyin.',
  },
  producer: {
    title: 'Üretici Detay Kartı',
    otherListings: (n: number) => `Kullanıcının Diğer İlanları (${n})`,
    // Trust badges, keyed by the backend users.badge_key value. Label +
    // supporting note for the producer-profile trust panel.
    trustBadges: {
      newProducer: { label: 'Yeni Üretici', note: 'Platforma ilan eklemeye başladı' },
      active: { label: 'Aktif Üretici', note: 'Düzenli olarak ilan paylaşıyor' },
      trusted: { label: 'Güvenilir Üretici', note: 'İlanları alıcılar tarafından beğeniliyor' },
      popular: {
        label: 'Popüler Üretici',
        note: 'Profili çok sayıda alıcının favorilerinde',
      },
    },
  },
  chat: {
    inboxTitle: 'Gelen Mesaj Kutusu',
    // Shown in place of a name when the other person deleted their account.
    // Their messages stay — the conversation is the survivor's record too.
    deletedUser: 'Silinmiş kullanıcı',
    conversationCount: (n: number) => `${n} Görüşme`,
    inputPlaceholder: 'Yazmaya başlayın…',
    send: 'Gönder',
    back: 'Geri',
    online: 'Çevrimiçi',
    offline: 'Çevrimdışı',
    noMessagesYet: 'Henüz mesaj yok. İlk mesajı siz gönderin.',
    emptyTitle: 'Henüz mesajınız yok',
    emptyBody: 'Bir ilana göz atın ve üreticiye "Mesaj Gönder" ile ulaşın.',
    emptyCta: 'İlanlara Göz At',
    loadOlder: 'Daha eski mesajlar',
    loadError: 'Mesajlar yüklenemedi.',
    notFound: 'Görüşme bulunamadı.',
    retry: 'Tekrar dene',
    sendFailed: 'Gönderilemedi',
    // The chat is frozen when either side is banned/deactivated (backend 403).
    blocked: 'Bu görüşmeye şu anda mesaj gönderilemiyor.',
    menu: 'Diğer seçenekler',
    menuBlock: 'Engelle',
    menuBlockAndReport: 'Engelle ve Bildir',
    menuUnblock: 'Engeli kaldır',
    menuReport: 'Bildir',
    blockConfirmTitle: 'Bu kişi engellensin mi?',
    blockConfirmBody:
      'Engellediğinizde ikiniz de bu görüşmeye mesaj gönderemezsiniz. Karşı tarafa bildirilmez. Engeli istediğiniz zaman kaldırabilirsiniz.',
    blockConfirmCta: 'Engelle',
    blockAndReportConfirmCta: 'Engelle ve Bildir',
    cancel: 'Vazgeç',
    blockedByMe: 'Bu kişiyi engellediniz.',
    // The thread stays readable; nothing can be delivered to an account that is gone.
    peerDeletedReadOnly:
      'Bu hesap silinmiş. Eski mesajları okuyabilirsiniz, ancak yanıt gönderilemez.',
    unblock: 'Engeli kaldır',
    blockFailed: 'İşlem tamamlanamadı. Lütfen tekrar deneyin.',
    selectTitle: 'Şikayet ettiğiniz mesajları seçin',
    selectedMessages: 'Seçilen mesajlar',
    selectedCount: (n: number) => (n === 0 ? 'Henüz mesaj seçmediniz.' : `${n} mesaj seçildi`),
    selectLimit: (max: number) => `En fazla ${max} mesaj seçebilirsiniz.`,
    reportDescription: 'Açıklama',
    reportDescriptionPlaceholder: 'Yaşadığınız sorunu kısaca anlatın…',
    reportSend: 'Gönder',
    reportCancel: 'Vazgeç',
    reportNeedsMessage: 'Lütfen en az bir mesaj seçin.',
    reportFailed: 'Bildiriminiz gönderilemedi. Lütfen tekrar deneyin.',
    reportAlreadyOpen: 'Bu kişi hakkındaki bildiriminiz zaten inceleniyor.',
    reportTooMany: 'Çok fazla bildirim gönderdiniz. Lütfen bir süre sonra tekrar deneyin.',
    reportUnavailable:
      'Seçtiğiniz mesajlar artık bildirilemiyor. Sayfayı yenileyip tekrar seçmeyi deneyin.',
    reportNeedsPeerMessage: 'Karşı tarafın en az bir mesajını seçin.',
    menuReportPending: 'Bildiriminiz inceleniyor',
    reportSent: 'Bildiriminiz iletildi.',
    selectMessage: 'Mesajı seç',
    today: 'Bugün',
    yesterday: 'Dün',
    // One conversation per person; the listing is a tag on the message that
    // opened the topic.
    aboutListing: (title: string) => `İlgili ilan: ${title}`,
    clearListingTag: 'İlan bağlantısını kaldır',
    listingTagFallback: 'İlan',
  },
  report: {
    headerTitle: 'Bize Ulaşın & Bildirim Gönder',
    headerSubtitle: 'Görüş, öneri ve karşılaştığınız sorunları buradan bize ulaştırabilirsiniz.',
    noticeTitle: 'Geri Bildirimleriniz Çok Değerli',
    noticeBody:
      'Uygulama deneyiminiz hakkındaki olumlu veya olumsuz düşünceleriniz, yapacağımız geliştirmelere yön verir.',
    categoryLabel: 'Bildirim Kategorisi',
    category: {
      feedback: 'Görüş Bildir',
      issue: 'Sorun Bildir',
      suggestion: 'Öneri',
    },
    issueTypeLabel: 'Sorun Türü',
    issueType: {
      product: 'Hatalı İlan',
      profile: 'Hatalı Profil',
      location: 'Hatalı Konum',
      app: 'Uygulama Hatası',
      user: 'Kullanıcı Şikayeti',
    },
    photosLabel: 'Ekran Görüntüsü',
    addPhoto: 'Görsel Ekle',
    removePhoto: 'Görseli kaldır',
    photoAlt: 'Bildirime eklenen ekran görüntüsü',
    openPhoto: 'Görseli büyüt',
    closePhoto: 'Kapat',
    photoCheckHint:
      'Görsele dokunarak kontrol edin. Sorunu göstermiyorsa kaldırıp kendi ekran görüntünüzü ekleyin.',
    descriptionLabel: 'Geri Bildirim Açıklaması',
    descriptionPlaceholder:
      'Platform hakkındaki genel deneyiminizi, görüşlerinizi veya memnun kaldığınız noktaları açıklayın…',
    submit: 'Bildirimi İlet',
    submitting: 'Gönderiliyor…',
    submitError: 'Lütfen göndermeden önce bir açıklama yazın.',
    issueTypeError: 'Lütfen sorunun türünü seçin.',
    sendError: 'Bildiriminiz gönderilemedi. Lütfen tekrar deneyin.',
    sent: 'Bildiriminiz iletildi. Bildirimlerim sekmesinden takip edebilirsiniz.',
    /**
     * "Çözüldü" bir soruna uyar ama bir görüşe veya öneriye tuhaf düşer —
     * bu ikisi üç kategorinin hepsinde doğru.
     */
    status: {
      open: 'İnceleniyor',
      resolved: 'Yanıtlandı',
    },
    filters: {
      all: 'Tümü',
      open: 'İnceleniyor',
      resolved: 'Yanıtlandı',
    },
    answerTitle: 'Yanıtımız',
    // A user complaint, as its reporter reads it back in Bildirimlerim.
    targetLabel: 'Şikayet edilen:',
    listingLabel: 'İlgili ilan:',
    listingDeleted: 'Silinmiş ilan',
    listingTag: (title: string) => `İlgili ilan: ${title}`,
    clearListing: 'İlan bağlantısını kaldır',
    listingFallback: 'İlan',
    listingAlreadyReported: 'Bu ilan için bildiriminiz zaten inceleniyor.',
    listingGone: 'Bu ilan artık mevcut değil.',
    pickedMessages: (n: number) => `Seçtiğiniz mesajlar (${n})`,
    purgedMessages: (n: number) => `${n} mesaj silindi`,
    you: 'Siz',
    yourDescription: 'Açıklamanız',
    tabsLabel: 'Bildir bölümleri',
    tabNew: 'Yeni Bildirim',
    tabMine: 'Bildirimlerim',
    tabBadge: (n: number) => `${n} okunmamış yanıt`,
    listTitle: (n: number) => `Bildirimlerim (${n})`,
    previousEmpty: 'Henüz bir bildiriminiz yok',
    previousEmptyBody:
      'Bir sorun, görüş veya öneriniz olduğunda Yeni Bildirim sekmesinden iletin; yanıtları burada görürsünüz.',
    previousEmptyFilteredTitle: 'Bu durumda bildirim yok',
    previousEmptyFilteredBody: 'Farklı bir durum seçerek diğer bildirimlerinizi görebilirsiniz.',
    previousEmptyFiltered: 'Bu durumda bir bildiriminiz yok.',
    loadError: 'Bildirimleriniz yüklenemedi. Lütfen sayfayı yenileyin.',
  },
  /**
   * Name and phone visibility on the account tab. Both start hidden. The
   * button says what pressing it does; the note under the field says the state.
   */
  privacy: {
    show: 'Göster',
    hide: 'Gizle',
    actionLabel: (field: string, action: string) => `${field} ${action.toLocaleLowerCase('tr')}`,
    // A state, not an event — both fields start hidden, so "gizlendi" would
    // describe something the user never did. gizli / herkese açık is the pair
    // users know from other apps' privacy settings. The name keeps its mask.
    nameHiddenNote: (masked: string) => `Adınız gizli (${masked}).`,
    namePublicNote: 'Adınız herkese açık.',
    phoneHiddenNote: 'Numaranız gizli.',
    phonePublicNote: 'Numaranız herkese açık.',
    saved: 'Gizlilik ayarınız kaydedildi.',
    saveFailed: 'Gizlilik ayarınız kaydedilemedi. Lütfen tekrar deneyin.',
  },
  settings: {
    title: 'Ayarlar',
  },
  profile: {
    tabs: {
      account: 'Hesap Bilgileri',
      listings: (n: number) => `İlanlarım (${n})`,
      favorites: (n: number) => `Favorilerim (${n})`,
      badges: 'Rozetlerim',
    },
    header: {
      logout: 'Çıkış Yap',
    },
    account: {
      sectionTitle: 'Hesap Bilgilerim',
      name: 'Ad - Soyad',
      phone: 'Telefon Numarası',
      email: 'E-Posta Adresi',
      // Email is optional at signup, so the profile is where it gets added.
      // Editable only until it is verified — after that it is the account's
      // recovery address and swapping it would be a takeover.
      // Badge in the label row, beside where "Doğrulandı" sits. Short by
      // necessity — the slot shares that row with the label.
      emailMissing: 'E-posta eklenmedi',
      emailAddHint: 'Daha iyi iletişim için e-posta ekleyin.',
      emailUnverifiedHint: 'Doğrulanana kadar e-posta adresinizi değiştirebilirsiniz.',
      emailSave: 'E-postayı Kaydet',
      emailSaved: 'E-posta adresiniz kaydedildi. Doğrulama bağlantısı gönderildi.',
      emailTaken: 'Bu e-posta adresi başka bir hesapta kayıtlı.',
      emailError: 'E-posta güncellenemedi. Lütfen tekrar deneyin.',
      location: 'Konum',
      update: 'Bilgileri Güncelle',
      updateSuccess: 'Konum bilgileriniz güncellendi.',
      updateError: 'Güncelleme başarısız oldu. Lütfen tekrar deneyin.',
      prefsTitle: 'Uygulama Tercihleri',
      notifTitle: 'Bildirim Ayarları',
      notifSheetHint: 'Almak istemediğiniz bildirimlerin işaretini kaldırın.',
      notifGroups: {
        messages: { title: 'Mesajlar', desc: 'Yeni mesaj geldiğinde' },
        myListings: {
          title: 'İlanlarım',
          desc: 'Onay, ret, süre hatırlatması ve favorilere eklenme',
        },
        following: { title: 'Favorilerim', desc: 'Favori ilan ve üreticilerdeki yenilikler' },
        reports: { title: 'Sorun Bildir', desc: 'Gönderdiğiniz bildirimlere yanıt verildiğinde' },
      },
      notifSaveError: 'Bildirim tercihi kaydedilemedi. Lütfen tekrar deneyin.',
      shotTitle: 'Otomatik Ekran Görüntüsü',
      shotDesc: 'Sorun bildirirken bulunduğunuz ekran otomatik eklenir',
      supportTitle: 'Sorun Bildir & Destek Al',
      supportDesc: 'Konum şikayetleri ve uygulama hataları',
    },
    /*
     * Hesap Durumu. Two actions of very different weight,
     * so they are separated: pausing is reversible and reads as a setting,
     * deleting is permanent and sits apart, below, in the destructive tone.
     */
    status: {
      sectionTitle: 'Hesap Durumu',
      deactivateTitle: 'Hesabımı pasife al',
      // States exactly what pausing does. It does NOT promise that nobody can
      // reach you: a buyer holding your number can still call, and existing
      // message threads keep working.
      deactivateDesc:
        'İlanlarınız yayından kalkar, bilgileriniz silinmez. İstediğiniz zaman geri açabilirsiniz.',
      deactivateConfirmTitle: 'Hesabınız pasife alınsın mı?',
      deactivateConfirmBody:
        'İlanlarınız ve üretici profiliniz uygulamada görünmeyecek. Giriş yapmaya devam edebilir, istediğiniz an hesabınızı tekrar aktif edebilirsiniz.',
      deactivateConfirm: 'Evet, pasife al',
      // The page button starts the action; only the dialog's question gets "Evet".
      deactivateAction: 'Pasife al',
      activateAction: 'Aktif et',
      activateTitle: 'Hesabımı tekrar aktif et',
      activateDesc: 'İlanlarınız yeniden yayına girer.',
      activateConfirmTitle: 'Hesabınız aktif edilsin mi?',
      activateConfirmBody: 'İlanlarınız ve profiliniz yeniden görünür olacak.',
      activateConfirm: 'Evet, aktif et',
      banner: 'Hesabınız pasif durumda. İlanlarınız yayında değil.',
      bannerAction: 'Aktif Et',
      error: 'İşlem tamamlanamadı. Lütfen tekrar deneyin.',

      deleteTitle: 'Hesabımı sil',
      deleteDesc: 'Hesabınız ve tüm ilanlarınız kalıcı olarak silinir. Bu işlem geri alınamaz.',
      deleteHeading: 'Hesabımı kalıcı olarak sil',
      // Every consequence named, because none of them can be undone. The chat
      // sentence is here because it is the one thing that does NOT disappear,
      // and a user has a right to know that before deciding.
      deleteWarning:
        'Hesabınız, ilanlarınız ve fotoğraflarınız kalıcı olarak silinecek. Mesajlaştığınız kişilerde konuşmalar kalır, siz "Silinmiş kullanıcı" olarak görünürsünüz. Bu işlem geri alınamaz.',
      deletePhoneLabel: 'Onaylamak için telefon numaranızı yazın',
      deletePhoneHint: 'Kayıtlı numaranız ile aynı olmalı.',
      deletePasswordLabel: 'Şifreniz',
      deleteGoogleNote:
        'Hesabınız Google ile açıldığı için şifre yerine Google ile doğrulama yapmanız gerekir.',
      // Verifying and deleting are two steps: the user confirms the deletion
      // themselves after coming back from Google, so the label promises only
      // the verification.
      deleteGoogleAction: 'Google ile doğrula',
      deleteGoogleVerified: 'Google doğrulaması tamamlandı. Silmek için aşağıdaki butonu kullanın.',
      // The failures the Google callback can redirect back with. Each one asks
      // something different of the user, so none of them is "try again".
      deleteReauthFailed: 'Google doğrulaması tamamlanamadı. Lütfen tekrar deneyin.',
      deleteReauthSession: 'Oturumunuz sona ermiş. Lütfen tekrar giriş yapıp deneyin.',
      deleteReauthMismatch:
        'Farklı bir Google hesabı seçtiniz. Hesabınızın bağlı olduğu Google hesabıyla doğrulayın.',
      deleteReauthExpired: 'Google doğrulamasının süresi doldu. Lütfen yeniden doğrulayın.',
      deleteSubmit: 'Hesabımı kalıcı olarak sil',
      deleteCancel: 'Vazgeç',
      deleteConfirmTitle: 'Hesabınız kalıcı olarak silinsin mi?',
      deleteConfirmBody:
        'Bu işlem geri alınamaz. Hesabınız, ilanlarınız ve fotoğraflarınız silinecek ve oturumunuz kapanacak.',
      deleteConfirmAction: 'Evet, hesabımı sil',
      deletePhoneMismatch: 'Telefon numarası kayıtlı numaranızla eşleşmiyor.',
      deleteWrongPassword: 'Şifreniz hatalı.',
      deleteError: 'Hesap silinemedi. Lütfen tekrar deneyin.',
    },
    listings: {
      viewLabel: 'Aktif veya pasif ilanlar',
      rejectionReason: 'Reddetme nedeni: ',
      rejectedNextStep: 'Düzenleyip yeniden onaya gönderebilirsiniz.',
      empty: 'Henüz ilanınız yok',
      emptyBody:
        'İlk ilanınızı yayınlayın; ürününüz arama sonuçlarında alıcıların karşısına çıksın.',
      createNudge: 'İlan Ver',
      // An empty side of the switch gets a way back out, unlike "henüz ilanınız yok".
      manageEmpty: 'Pasif ilanınız yok',
      manageEmptyBody:
        'Duraklattığınız, süresi dolan ve yayından kaldırılan ilanlar burada toplanır.',
      manageEmptyCta: 'Aktif ilanlara dön',
      activeEmpty: 'Aktif ilanınız yok',
      activeEmptyBody: 'Yayındaki ve onay bekleyen ilanlarınız burada görünür.',
      activeEmptyCta: 'Pasif ilanlara bak',
      harvest: 'Hasat',
      endsOn: (date: string) => `Bitiş: ${date}`,
      pause: 'Pasif Yap',
      resume: 'Yayına Al',
      pauseError: 'İlan duraklatılamadı. Lütfen tekrar deneyin.',
      /*
       * Claims ONLY what pausing actually guarantees. Pausing hides the listing, it does not stop a
       * buyer who already has the number from calling, and existing chat
       * threads keep working. All we can honestly say is that it is not live
       * until the seller puts it back.
       */
      pausedNote: 'Yayına alana kadar ilanınız yayında değil.',
      edit: 'Düzenle',
      delete: 'Sil',
      // Names the listing: the card is gone, so the message is the only
      // record of which one was deleted.
      deletedSuccess: (title: string) => `${title} silindi.`,
      deleteError: 'İlan silinemedi. Lütfen tekrar deneyin.',
      // Renewal — an expired listing goes passive on its own and the
      // producer re-publishes it from here.
      renew: 'Yeniden Yayınla',
      renewTitle: 'İlanı Yeniden Yayınla',
      renewBody: 'İlanınız hangi süre boyunca tekrar yayında kalsın?',
      // NOT "Yayına Al" — that is the resume button's label, and the same
      // words on two different actions is what made the second step read as
      // a repeat of the first.
      renewConfirm: 'Süreyi Uzat',
      renewMonths: (n: number) => `${n} Ay`,
      // Names the listing: the renewed card leaves the Pasif view immediately,
      // so the message is the only thing left confirming what happened.
      renewSuccess: (title: string) => `"${title}" tekrar yayında.`,
      pausedSuccess: (title: string) => `"${title}" duraklatıldı.`,
      // Only fires when resuming actually made the listing live. If expiry is
      // still blocking it, the seller gets the step-2 dialog instead and no
      // toast at all — two things to read at once is one too many.
      resumedSuccess: (title: string) => `"${title}" yayına alındı.`,
      renewError: 'İlan yeniden yayınlanamadı. Lütfen tekrar deneyin.',
      // 403 here means the account may not publish — retrying will never
      // help, so the generic "try again" would leave the producer stuck.
      renewForbidden: 'İlan yayınlama yetkiniz bulunmuyor. Destek ile iletişime geçin.',
      expiredOn: (date: string) => `${date} tarihinde sona erdi`,
      // Early renewal of a still-live listing, offered in its last three days.
      extend: 'Süreyi Uzat',
      extendTitle: 'İlan Süresini Uzat',
      extendBody: (title: string, date: string) =>
        `${title} ilanınız ${date} tarihinde sona eriyor. Ne kadar uzatılsın?`,
      extendNewEnd: (date: string) => `Yeni bitiş: ${date}`,
      extendSuccess: (title: string, date: string) => `"${title}" ${date} tarihine kadar yayında.`,
      extendError: 'İlan süresi uzatılamadı. Lütfen tekrar deneyin.',
      removedByAdmin: 'Bu ilan yönetici tarafından yayından kaldırıldı.',
      removedByAdminLabel: 'Yönetici tarafından kaldırıldı: ',
    },
    favorites: {
      empty: 'Henüz favori ilanınız yok',
      emptyBody:
        'Beğendiğiniz ilanları kalp simgesiyle kaydedin; hepsini burada tek yerde görürsünüz.',
      emptyCta: 'İlanlara Göz At',
      unavailable: 'Şu an yayında değil',
    },
    badges: {
      sectionTitle: 'Kullanıcı Rozet Durumları',
      earned: 'Tamamlandı',
      locked: 'Kilitli',
      openState: 'Açık',
    },
    // "Pasif" split into two: they are different problems with different
    // fixes — Duraklatıldı is the seller's own choice, Süresi Doldu is the
    // clock running out.
    state: {
      active: 'Aktif',
      paused: 'Duraklatıldı',
      expired: 'Süresi Doldu',
      pending: 'Onay Bekliyor',
      rejected: 'Reddedildi',
      removed: 'Yayından Kaldırıldı',
    },
  },
  notifications: {
    title: 'Bildirimler',
    bell: 'Bildirimler',
    bellUnread: (count: number) => `Bildirimler, ${count} okunmamış`,
    readAll: 'Tümünü okundu işaretle',
    emptyTitle: 'Henüz bildirim yok',
    emptyBody: 'Mesajlarınız, ilanlarınız ve favorilerinizle ilgili gelişmeler burada görünür.',
    errorTitle: 'Bildirimler yüklenemedi',
    errorBody: 'Bağlantınızı kontrol edip tekrar deneyin.',
    retry: 'Tekrar dene',
  },
  // Destructive-action prompts. Every entry is a ConfirmOptions, passed straight
  // to confirm().
  confirm: {
    reportListing: {
      title: 'Bu ilanı bildirmek istiyor musunuz?',
      confirmLabel: 'Bildir',
      cancelLabel: 'Vazgeç',
      tone: 'primary' as const,
    },
    logout: {
      title: 'Çıkış yapılsın mı?',
      body: 'Hesabınızdan çıkış yapacaksınız.',
      confirmLabel: 'Çıkış Yap',
      cancelLabel: 'Vazgeç',
    },
    // Pausing takes the listing off the map, so it is confirmed; resuming is
    // not. The body names the one thing sellers get wrong — the clock does
    // not stop while they are paused.
    pauseListing: {
      title: 'İlan duraklatılsın mı?',
      // Same honesty rule as pausedNote: only the guarantee we can keep.
      body: 'İlanınız yayından kaldırılacak ve siz yayına alana kadar görünmeyecek. İstediğiniz zaman tekrar yayına alabilirsiniz.',
      confirmLabel: 'Pasif Yap',
      cancelLabel: 'Vazgeç',
      tone: 'primary' as const, // reversible in one click — not delete-red
    },
    /*
     * Step 2, shown alone after resuming a listing that expired while paused.
     * The CTA is "Süre Yenile", NOT "Yeniden Yayınla": the listing has just
     * been put back on, so nothing is being re-published — only the validity
     * period is dead. Reusing the card's label here made the popup look like
     * it was repeating the button the seller had already pressed.
     */
    expiredAfterResume: {
      title: 'İlan yayına alındı',
      body: 'Ancak geçerlilik süresi dolduğu için ilanınız hâlâ görünmüyor. Süreyi yenilediğinizde yayına çıkacak.',
      confirmLabel: 'Süre Yenile',
      cancelLabel: 'Daha Sonra',
      tone: 'primary' as const,
    },
    logoutWithListing: {
      title: 'Çıkış yapılsın mı?',
      body: 'Kaydedilmemiş ilan bilgileriniz silinecek.',
      confirmLabel: 'Yine de Çık',
      cancelLabel: 'Vazgeç',
    },
    logoutWithEdits: {
      title: 'Çıkış yapılsın mı?',
      body: 'İlanınızda kaydedilmemiş değişiklikler var, silinecek.',
      confirmLabel: 'Yine de Çık',
      cancelLabel: 'Vazgeç',
    },
    discardEdits: {
      title: 'Değişiklikler kaybolacak',
      body: 'Girdiğiniz bilgiler kaydedilmedi.',
      confirmLabel: 'Sayfadan Ayrıl',
      cancelLabel: 'Düzenlemeye Dön',
    },
    discardPhotos: {
      title: 'Fotoğraflar kaydedilmedi',
      body: 'Ayrılırsanız fotoğrafları tekrar eklemeniz gerekecek; metin bilgileriniz taslak olarak saklanır.',
      confirmLabel: 'Yine de Ayrıl',
      cancelLabel: 'Düzenlemeye Dön',
    },
    clearForm: {
      title: 'Form temizlensin mi?',
      body: 'Girdiğiniz tüm bilgiler ve taslak silinecek.',
      confirmLabel: 'Temizle',
      cancelLabel: 'Vazgeç',
    },
    deleteListing: {
      title: 'İlan silinsin mi?',
      // Points at Pasif Yap: a producer who only wants a break should not
      // lose the listing, its photos and its favorites for good.
      body: 'İlanınız, fotoğrafları ve favorilere eklenme bilgisi kalıcı olarak silinecek; bu işlem geri alınamaz. Yalnızca bir süre yayından kaldırmak istiyorsanız Pasif Yap seçeneğini kullanın.',
      confirmLabel: 'Sil',
      cancelLabel: 'Vazgeç',
    },
    deletePhoto: {
      title: 'Fotoğraf silinsin mi?',
      body: 'Bu fotoğraf ilandan kalıcı olarak kaldırılacak.',
      confirmLabel: 'Sil',
      cancelLabel: 'Vazgeç',
    },
  },
} as const;
